/**
 * Ness Frame Character 상수 및 핵심 로직 (Shared)
 */
import { BASE_PRODUCT_MAKER_TYPES } from '@modules/BaseProduct/S/Constant/baseproduct';
import type { AssocBpOption } from '@modules/BpOption/S/Function/bp-option.util';
import { NessUtilS } from '@modules/Ness/Util/S/Function/module/NessUtilS';
import { BpOptions, SelectedOptionGroups } from '@services/database/mco/schema/public';

// ============================================
// Types & Interfaces
// ============================================
export interface FrameCharacter {
  /** 캐릭터 고유 식별자 */
  gcid: string;
  /** 게임 월드 ID */
  world_id: string;
  /** 메이플 유저 계정 아이디 */
  guid: string;
  /** 게임 캐릭터 이름 */
  gc_name: string;
  maple_image_url: string;
  checker: {
    warning: { width: boolean; height: boolean };
    impossible: { width: boolean; height: boolean };
  };
}

export type NessAssocSelectedOptionGroup = SelectedOptionGroups & {
  _: {
    bp_options: AssocBpOption[];
  };
};

export type NessFrameDetails = {
  type: 'NESS_CHARACTER_INFO';
  value: {
    frame_characters: FrameCharacter[];
  };
} | null;

function mmToPx(mm) {
  return Math.round(mm * 2.786);
}

// ============================================
// Constants
// ============================================
export const NessFrameProduct = {
  isFrameBp: (base_product: { maker_type: string }) => {
    return base_product?.maker_type === BASE_PRODUCT_MAKER_TYPES.NESS_CHARACTER_FRAME;
  },
  pickObject: (uc: FrameCharacter) => {
    return {
      gcid: uc.gcid,
      /** 게임 월드 ID */
      world_id: uc.world_id,
      /** 메이플 유저 계정 아이디 */
      guid: uc.guid,
      /** 게임 캐릭터 이름 */
      gc_name: uc.gc_name,
      // maple_image_url: NessFrameProduct.makeDefaultMotionUrl(uc.maple_image_url),
      maple_image_url: uc.maple_image_url,
      checker: uc.checker,
    };
  },
  makeDefaultMotionUrl: (maple_image_url: string) => {
    return NessUtilS.makeQueryParams(maple_image_url, {
      emotion: 'E00',
      action: 'A00',
      wmotion: 'W04',
    });
  },
  frame_option: {
    group_name: '캐릭터 선택창 액자',
    product_detail_sub_title: '액자 사이즈 45 x 39 cm',
  },
  character_option: {
    group_name: '전용 캐릭터 아크릴 스탠드',
  },
  second_name: '캐릭터 선택창 액자 45 x 39cm + 전용 캐릭터 아크릴 스탠드',
  BP_OPTIONS: {
    NESS_FRAME_SELECTED: 'NESS_FRAME_SELECTED',
    NESS_FRAME_NOT_SELECTED: 'NESS_FRAME_NOT_SELECTED',
  },
  validateForCart: (details: NessFrameDetails, bp_options: BpOptions[]) => {
    if (
      !bp_options?.length ||
      !bp_options.some(
        ({ dev_name }) =>
          dev_name &&
          [
            NessFrameProduct.BP_OPTIONS.NESS_FRAME_SELECTED,
            NessFrameProduct.BP_OPTIONS.NESS_FRAME_NOT_SELECTED,
          ].includes(dev_name),
      )
    ) {
      throw new Error('액자 관련 옵션이 선택되지 않았습니다.');
    }
    const character_bp_option = bp_options.find(({ dev_name }) => parseInt(dev_name) > 0);
    if (character_bp_option || details) {
      if (!details) {
        throw new Error('캐릭터 정보가 없습니다.');
      }
      if (!character_bp_option?.dev_name) {
        throw new Error('캐릭터 카운트 정보가 없습니다.');
      }
      const length = details.value.frame_characters.length;
      if (length !== parseInt(character_bp_option.dev_name)) {
        throw new Error('캐릭터 수량과 카운트 정보가 맞지 않습니다.');
      }
    }
  },
  canvas_option: {
    // 가장 중요한것은 : 1mm 가 2.786px 임
    /* 1254px = 450 mm  -> 2.786 px_per_mm */
    character: {
      crop_size: { w: 300, h: 188 },
      crop_start_position: { x: 0, y: 0 },
      move_position: { x: 0, y: 0 },
      scale_size: {
        // 300px -> 275mm
        // w: Math.round(2.786 * 275),
        // h: Math.round(2.786 * 172),
        w: 766,
      },
      checker: {
        warning: { widthMm: 68, heightMm: 91 },
        impossible: { widthMm: 280, heightMm: 154 },
      },
    },
    text: {
      // 가장 중요한것은 : 1mm 가 2.786px 임 그렇다면
      /* padding 1.88, font 4.84*/
      font_family: 'Morris',
      font_url: 'https://s3.marpple.co/font/ness/maplestory/morris.woff',
      font_size: mmToPx(4.84),
      background_color: '#353535',
      height: mmToPx(4.84),
      padding_left: mmToPx(1.88),
      padding_right: mmToPx(1.88),
      padding_top: mmToPx(1.88),
      padding_bottom: mmToPx(1.88),
      corner_margin: mmToPx(0.44),
    },
    frame: {
      offsets: {
        x: 16.79, // 캐릭터들간 x 축 이것을 통해서 양 옆으로 캐릭터들의 간격이 완성
        y_first: 90, // 1층 잘 맞춰놓음
        y_second: 394, // 2층 잘 맞춰놓음
      },
    },
    images: {
      frame: {
        without_frame:
          '//s3.marpple.co/files/u_193535/2025/12/original/cfada20c717329a8533140d61ef220253f0710603.png',
        with_frame:
          '//s3.marpple.co/files/u_193535/2025/12/original/330730af025dca778b2d526ea155e46ef98dde642.png',
        top_frame_layer:
          '//s3.marpple.co/files/u_193535/2025/12/original/69df26f3e57a0beb29d3f43daaa14b85ad040c571.png',
      },
      character: {
        hollow_character_image_url:
          '//s3.marpple.co/files/u_5246831/2025/8/original/5ebdc61513f12c65b632f1006e02c94cbe1581fc1.png',
      },
    },
  },
};

export const calculateRetailPrice = (frame_bp_option: AssocBpOption, character_bp_option: AssocBpOption) => {
  return {
    frame_bp_option_price: frame_bp_option.payment_price,
    character_bp_option_price: character_bp_option.payment_price,
  };
};
